import { EventEmitter, Injectable, Type, ViewContainerRef } from '@angular/core';
import { ModalComponent } from '../core/components/modal/modal.component';

/**
 * Interface for modal components that return a result when closed.
 * @template T The type of result returned by the modal.
 */
export interface IModal<T = unknown> {
  /** Event emitted when the modal is closed. */
  closed: EventEmitter<T>;
}

/**
 * Type for modal configuration options.
 * @template T The type of value returned when the modal is closed.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type ModalOptions<T = boolean> = {
  /** Optional title for the modal */
  title?: string;
  /** Optional message to display in the modal body */
  message?: string;
  /**
   * Flag indicating if the modal is a confirmation modal.
   * If true, the modal will display “OK” and “Cancel” buttons.
   */
  isConfirm?: boolean;
  /**
   * Custom component to render inside the modal.
   * If provided, the modal will render this component instead of the default content.
   */
  customComponent?: Type<IModal<T>>;
  /**
   * Optional inputs to pass to the custom component.
   */
  customComponentInputs?: { [key: string]: unknown };
};

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  /** The container where modals will be rendered */
  private containerRef: ViewContainerRef | null = null;

  /**
   * Registers the container ViewContainerRef.
   * @param viewContainerRef - The container's ViewContainerRef.
   */
  public registerContainer(viewContainerRef: ViewContainerRef): void {
    this.containerRef = viewContainerRef;
  }

  /**
   * Opens a default modal dialog.
   * @param options - Options for configuring the default modal.
   * @returns A Promise that resolves to a boolean value.
   */
  public async open(options: ModalOptions<boolean>): Promise<boolean>;

  /**
   * Opens a custom modal dialog with a type-safe return value.
   * @param options - Options for configuring the custom modal.
   * @returns A Promise that resolves to a value of type T.
   */
  public async open<T>(options: ModalOptions<T>): Promise<T>;

  public async open<T = boolean>(options: ModalOptions<T>): Promise<T> {
    if (!this.containerRef) {
      return Promise.reject('Modal container is not registered.');
    }

    // Clear previous modal(s) if any.
    this.containerRef.clear();

    // If a custom component is provided, create it directly.
    if (options.customComponent) {
      const componentRef = this.containerRef.createComponent(options.customComponent);

      // Pass custom inputs to the component if provided.
      if (options.customComponentInputs) {
        Object.assign(componentRef.instance, options.customComponentInputs);
      }

      return new Promise<T>((resolve) => {
        const subscription = componentRef.instance.closed.subscribe((result: T) => {
          resolve(result);
          subscription.unsubscribe();
          componentRef.destroy();
        });
      });
    } else {
      // Otherwise, create the default ModalComponent which returns a boolean.
      const componentRef = this.containerRef.createComponent(ModalComponent);
      componentRef.instance.title = options.title || '';
      componentRef.instance.message = options.message || '';
      componentRef.instance.isConfirm = options.isConfirm || false;

      return new Promise<T>((resolve) => {
        const subscription = componentRef.instance.closed.subscribe((result: boolean) => {
          // Cast result to T (safe because T defaults to boolean when no customComponent is provided)
          resolve(result as unknown as T);
          subscription.unsubscribe();
          componentRef.destroy();
        });
      });
    }
  }
}
